import * as helpers from './helpers'

// Amplitude "Web Experiment" (no-code / visual experimentation) loader.
//
// This is distinct from the code-based Experiment SDK that is already bundled
// in `@amplitude/unified` (the `experimentPlugin` added by `initAll`). Web
// Experiment is a standalone script that carries the DOM-mutation + visual
// editor + anti-flicker engine. Once on the page it auto-detects the Amplitude
// Browser SDK already initialized by this package and reports impression /
// exposure events through it — so it reuses the same project, device_id,
// user_id and session. No CDP integration shim is required.
//
// Amplitude recommends placing the script in <head> as high as possible to
// minimize flicker. In our client-rendered micro-frontends there is no shared
// server <head>, so we inject it at analytics init time as the first element
// in <head>. This is a dynamic (async) injection that runs after the app
// bundle executes, so it does NOT provide the parser-blocking anti-flicker of
// a raw <head> tag — see setUpWebExperimentScript below.
//
// Docs: https://amplitude.com/docs/web-experiment/implementation

const CDN_HOST = 'https://cdn.amplitude.com/script/'

const buildScriptSrc = (apiKey: string): string =>
  `${CDN_HOST}${apiKey}.experiment.js`

// Injects the Web Experiment script into <head>, inserted first so the
// anti-flicker engine can run before the page paints. Idempotent — a second
// call is a no-op if the script is already present.
function setUpWebExperimentScript(apiKey: string): void {
  const src = buildScriptSrc(apiKey)

  if (helpers.hasScriptAlreadyLoaded(`${apiKey}.experiment.js`)) {
    return
  }

  const scriptElement = document.createElement('script')
  // NOTE: This is a dynamically-injected script, which the browser treats as
  // async — it is NOT the parser-blocking synchronous <head> tag from
  // Amplitude's docs, nor the async anti-flicker snippet. Because it runs at
  // init() time (after the app bundle executes) it provides the weakest
  // anti-flicker guarantee: a control -> variant swap may be briefly visible.
  // For true anti-flicker, the raw <script> must live in the HTML <head>
  // before our JS bundle (edge/server shell).
  scriptElement.src = src

  const head = document.head || document.getElementsByTagName('head')[0]

  if (head) {
    head.insertBefore(scriptElement, head.firstChild)
  } else {
    // Fallback: insert before the first existing script tag.
    const firstScript = document.getElementsByTagName('script')[0]
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(scriptElement, firstScript)
    }
  }
}

export { setUpWebExperimentScript, buildScriptSrc }
